// biome-ignore-all lint/a11y: TODO - address a11y issues later
import {
  ArrowLeftCircle,
  ArrowRightCircle,
  Briefcase,
  Calendar,
  ExternalLink,
  Folder,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { CampaignHierarchy } from "@/components/campaign-hierarchy";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { SystemId } from "@/components/ui/system-id";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Activity } from "@/hooks/api/use-account-activities";
import { useActivityExternalConnectionLink } from "@/hooks/api/use-external-connection-links";
import type {
  MilestoneAnalysis,
  MilestoneAnalysisTouchpoint,
  TaskGroup,
} from "@/hooks/api/use-milestone-analyses";
import { useTouchpointPersons } from "@/hooks/api/use-touchpoint-persons";
import { useTouchpointIdSidebarEnabled } from "@/hooks/use-feature-flags";
import { formatDate } from "@/lib/date-formatting";
import { getExternalSystemName } from "@/lib/external-connection-links";
import type { CollapsedActivity } from "@/pages/accounts/account-explorer/touchpoints-table/types";
import { EmailThread } from "./email-thread";
import { MilestoneAnalysesSheet } from "./milestone-analysis";
import { TouchpointGroup } from "./touchpoint-group";
import { WebSession } from "./web-session";

// Type guard to check if an activity is a CollapsedActivity
export function isGroupedActivity(
  activity: Activity | CollapsedActivity | null,
): activity is CollapsedActivity {
  if (activity && "activities" in activity) {
    return Array.isArray(activity.activities);
  }
  return false;
}

interface ActivityDetailsSheetProps {
  activity: Activity | CollapsedActivity | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  accountId?: string;
  getPersonNameOverride?: (personId: string) => string;
  getPersonEmailOverride?: (personId: string) => string;
  getNameByEmailOverride?: (email?: string | null) => string;
  focusedActivityId?: string;
  allActivities?: Activity[];
  milestoneTask?: TaskGroup;
  onFilterCitedTouchpoints?: (taskId: string, touchpointIds: string[]) => void;
  hasCitedTouchpointsFilter: boolean;
  selectedAnalysis?: MilestoneAnalysis;
  setSelectedAnalysis?: (milestoneAnalysis: MilestoneAnalysis) => void;
}

function DetailItem({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) {
  if (value === null || value === undefined || value === "") return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-medium text-muted-foreground">{label}</h3>
      </div>
      <div className="text-sm whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function ActivityHeader({ activity }: { activity: Activity }) {
  const showTouchpointId = useTouchpointIdSidebarEnabled();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <h2 className="text-xl font-bold">
          {activity.activity || "Untitled Activity"}
        </h2>
      </div>
      <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
        <Badge
          variant="secondary"
          className="bg-accent hover:bg-accent text-accent-foreground"
        >
          {activity.channel}
        </Badge>
        {activity.status && (
          <>
            {" • "}
            <span className="flex items-center gap-1">{activity.status}</span>
          </>
        )}
        {activity.date && (
          <>
            {" • "}
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(activity.date)}
            </span>
          </>
        )}
        {activity.direction && (
          <>
            {" • "}
            <span className="flex items-center gap-1">
              {activity.direction === "IN" ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ArrowRightCircle className="h-3 w-3 text-primary" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Inbound</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ArrowLeftCircle className="h-3 w-3 text-muted-foreground/40" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Outbound</p>
                  </TooltipContent>
                </Tooltip>
              )}
              <span>
                {activity.direction === "IN" ? "Inbound" : "Outbound"}
              </span>
            </span>
          </>
        )}
        {showTouchpointId && (
          <span className="flex items-center gap-1">
            <SystemId id={activity.id} />
          </span>
        )}
      </div>
    </div>
  );
}

function PeopleList({
  people,
  accountId,
  getPersonNameOverride,
  getPersonEmailOverride,
}: {
  people: Array<{ id: string; role_in_touchpoint?: string }>;
  accountId?: string;
  getPersonNameOverride?: (personId: string) => string;
  getPersonEmailOverride?: (personId: string) => string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    getPersonName: getPersonNameFromApi,
    sortPeopleByTpRoleAndSelectionStatus,
  } = useTouchpointPersons(
    getPersonNameOverride || getPersonEmailOverride ? undefined : accountId,
  );
  const getPersonName = getPersonNameOverride ?? getPersonNameFromApi;

  // Ensure people is an array and not empty
  if (!people || !Array.isArray(people) || people.length === 0) return null;

  // Sort people by role and employee status
  const sortedPeople = sortPeopleByTpRoleAndSelectionStatus(people);

  // Make sure we have at least one person to display
  if (sortedPeople.length === 0) return null;

  const personNames = sortedPeople.map((person) => ({
    name: getPersonName(person.id),
    id: person.id,
  }));

  const displayCount = 3;
  const displayPeople = isExpanded
    ? personNames
    : personNames.slice(0, displayCount);

  return (
    <div className="rounded-md bg-secondary/50 p-2 mt-2 max-w-md">
      <div className="mb-1 text-xs text-muted-foreground">
        People ({personNames.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {displayPeople.map(({ name, id }) => (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              <Badge
                variant="secondary"
                className="flex items-center justify-center h-6 text-xs"
              >
                {name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Person ID: {id}</p>
            </TooltipContent>
          </Tooltip>
        ))}
        {!isExpanded && personNames.length > displayCount && (
          <button
            onClick={() => setIsExpanded(true)}
            className="text-sm text-muted-foreground hover:text-foreground shrink-0"
          >
            +{personNames.length - displayCount} more
          </button>
        )}
      </div>
    </div>
  );
}

export function ActivityDetailsSheet({
  activity,
  isOpen,
  onOpenChange,
  accountId,
  getPersonNameOverride,
  getPersonEmailOverride,
  getNameByEmailOverride,
  focusedActivityId,
  allActivities = [],
  milestoneTask,
  onFilterCitedTouchpoints,
  hasCitedTouchpointsFilter,
  selectedAnalysis,
  setSelectedAnalysis,
}: ActivityDetailsSheetProps) {
  const isGrouped = isGroupedActivity(activity);
  const externalLink = useActivityExternalConnectionLink({
    accountId,
    activity: !isGrouped ? activity : null,
  });
  const sourceRecord = getExternalSystemName(
    !isGrouped ? activity?.source_record_type || "" : "",
  );

  // Handler for filtering touchpoints based on cited evidence
  const handleFilterCitedTouchpoints = (
    taskId: string,
    touchpoints: MilestoneAnalysisTouchpoint[],
  ) => {
    if (onFilterCitedTouchpoints) {
      // Extract the touchpoint IDs from the activity evidence
      // Use either id or inferred_from_id as the touchpoint_id
      const touchpointIds = touchpoints
        .map((t) => t.inferred_from_id || t.id)
        .filter(Boolean); // Remove null values

      if (touchpointIds.length > 0) {
        onFilterCitedTouchpoints(taskId, touchpointIds);
      }
    }
  };

  const renderDetails = () => {
    // If we have a milestone analysis to display, prioritize it
    if (milestoneTask) {
      return (
        <MilestoneAnalysesSheet
          taskGroup={milestoneTask}
          onFilterCitedTouchpoints={handleFilterCitedTouchpoints}
          hasCitedTouchpointsFilter={hasCitedTouchpointsFilter}
          selectedAnalysis={selectedAnalysis || milestoneTask.analyses[0]}
          setSelectedAnalysis={setSelectedAnalysis || (() => {})}
        />
      );
    }

    if (!activity) return null;

    // For single activities (not collapsed groups)
    if (!isGrouped) {
      // Check if it's an email thread
      if (
        activity &&
        "record_type" in activity &&
        activity.record_type === "direct_email"
      ) {
        // When viewing an email message, always use the activity.id for the thread data
        // but pass the focusedActivityId (if available) for initial selection
        return (
          <EmailThread
            activity={activity}
            expandedEmailId={focusedActivityId || activity.id}
            accountId={accountId}
            getNameByEmailOverride={getNameByEmailOverride}
          />
        );
      }

      // Check if it's a web session
      if (
        activity &&
        "record_type" in activity &&
        activity.record_type === "web_page_view"
      ) {
        return <WebSession activity={activity} accountId={accountId} />;
      }

      // Regular activity details
      return (
        <div className="space-y-6 py-4">
          <ActivityHeader activity={activity} />

          <Separator className="my-4" />

          <div className="space-y-6 relative">
            {externalLink && (
              <div className="absolute top-0 right-0">
                <a
                  href={externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:bg-muted rounded-md px-2 py-1 flex items-center gap-1 text-sm"
                >
                  View in {sourceRecord}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
            {activity.team && (
              <DetailItem
                label="Team"
                value={activity.team}
                icon={<Briefcase className="h-4 w-4 text-supporting-purple" />}
              />
            )}

            {activity.people && activity.people.length > 0 && (
              <PeopleList
                people={activity.people}
                accountId={accountId}
                getPersonNameOverride={getPersonNameOverride}
                getPersonEmailOverride={getPersonEmailOverride}
              />
            )}

            {activity.campaign_id && (
              <DetailItem
                label="Campaign Hierarchy"
                value={<CampaignHierarchy campaignId={activity.campaign_id} />}
                icon={<Folder className="h-4 w-4 text-supporting-orange" />}
              />
            )}
          </div>
        </div>
      );
    }

    // For collapsed activity groups

    // Check if the collapsed group contains email threads
    if (
      activity.source_record_type === "direct_email" ||
      activity.thread_id ||
      activity.activities?.some(
        (act) =>
          act.record_type === "direct_email" ||
          act.source_record_type === "direct_email" ||
          act.thread_id,
      )
    ) {
      // For email threads:
      // 1. Always use the first activity for fetching thread data to avoid unnecessary network requests
      // 2. Pass focusedActivityId to highlight the selected email
      const firstActivity = activity.activities[0];

      return (
        <EmailThread
          activity={firstActivity}
          expandedEmailId={focusedActivityId || firstActivity.id}
          accountId={accountId}
          getNameByEmailOverride={getNameByEmailOverride}
        />
      );
    }

    // Check if the collapsed group contains web sessions
    if (
      activity.source_record_type === "web_page_view" ||
      activity.session_id ||
      activity.activities?.some(
        (act) =>
          act.record_type === "web_page_view" ||
          act.source_record_type === "web_page_view" ||
          act.session_id,
      )
    ) {
      // For web sessions, use the first activity as the representative
      const firstActivity = activity.activities[0];
      return <WebSession activity={firstActivity} accountId={accountId} />;
    }

    // Default collapsed activity rendering
    return (
      <TouchpointGroup
        collapsedActivity={activity}
        accountId={accountId}
        focusedActivityId={focusedActivityId}
        allActivities={allActivities}
        getPersonNameOverride={getPersonNameOverride}
        getPersonEmailOverride={getPersonEmailOverride}
      />
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <SheetContent className="bg-card">
        <div className="h-full overflow-y-auto pr-2">{renderDetails()}</div>
      </SheetContent>
    </Sheet>
  );
}
